import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Sport } from '__generated__/globalTypes';
import { FilterInDropdown } from 'atoms/dropdowns/FilterInDropdown';
import { CheckboxGroup } from 'atoms/inputs/CheckboxGroup';
import { LabelM } from 'atoms/typography';
import { useInGameNotificationContext } from 'contexts/inGameNotification';
import usePaginatedQuery from 'hooks/graphql/usePaginatedQuery';
import { useGetNotificationCategories } from 'hooks/notification/useGetNotificationCategories';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import useLifecycle from 'hooks/useLifecycle';
import { sportsLabelsMessages } from 'lib/glossary';

import { Notification } from '../Notification';
import {
  NotificationGroup,
  allGroupsButAnnouncements,
  messages,
} from '../constants';
import { FiltersContainer, ItemsContainer, Root } from '../ui';
import {
  GetNotificationsQuery,
  GetNotificationsQueryVariables,
} from './__generated__/index.graphql';

const GET_NOTIFICATIONS_QUERY = gql`
  query GetNotificationsQuery(
    $cursor: String
    $categories: [NotificationCategoryInput!]
    $sports: [Sport!]
  ) {
    currentUser {
      slug
      notifications: anyNotifications(
        after: $cursor
        categories: $categories
        sports: $sports
      ) {
        nodes {
          id
          ...Notification_notification
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${Notification.fragments.notification}
` as TypedDocumentNode<GetNotificationsQuery, GetNotificationsQueryVariables>;

const sports = Object.values(Sport);

export const Notifications = () => {
  const { markNotificationsAsRead } = useInGameNotificationContext();
  const [selectedGroups, setSelectedGroups] = useState<NotificationGroup[]>([]);
  const { lifecycle } = useLifecycle();
  const lastVisitedSport = lifecycle?.lastVisitedSport;
  const [selectedSports, setSelectedSports] = useState<Sport[]>(
    lastVisitedSport ? [lastVisitedSport] : []
  );
  const getCategories = useGetNotificationCategories();
  const { data, loading, loadMore } = usePaginatedQuery(
    GET_NOTIFICATIONS_QUERY,
    {
      variables: {
        sports: selectedSports,
        categories: getCategories(
          selectedGroups.length ? selectedGroups : allGroupsButAnnouncements
        ),
      },
      connection: 'NotificationInterfaceConnection',
    }
  );

  const notifications = data?.currentUser?.notifications;

  useEffect(() => {
    const read = notifications?.nodes?.filter(Boolean);
    if (read?.length) markNotificationsAsRead(read);
  }, [notifications?.nodes, markNotificationsAsRead]);

  const hasNextPage = Boolean(
    data?.currentUser?.notifications?.pageInfo?.hasNextPage
  );

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        cursor: data?.currentUser?.notifications?.pageInfo.endCursor,
      });
    },
    hasNextPage,
    loading
  );

  return (
    <Root>
      <FiltersContainer>
        <FilterInDropdown
          buttonLabel={
            <FormattedMessage
              id="Activity.Notifications.sports"
              defaultMessage="{nbSports, plural, =0 {All sports} other {Sports (#)}}"
              values={{
                nbSports: selectedSports.length,
              }}
            />
          }
          filterSelected={!!selectedSports.length}
          onClearFilter={() => setSelectedSports([])}
        >
          <CheckboxGroup
            options={sports.map(sport => ({
              value: sport,
              label: (
                <LabelM>
                  <FormattedMessage {...sportsLabelsMessages[sport]} />
                </LabelM>
              ),
            }))}
            selectedValues={selectedSports}
            onChange={setSelectedSports}
          />
        </FilterInDropdown>
        <FilterInDropdown
          buttonLabel={
            <FormattedMessage
              id="Activity.Notifications.categories"
              defaultMessage="{nbCategories, plural, =0 {All categories} other {Categories (#)}}"
              values={{
                nbCategories: selectedGroups.length,
              }}
            />
          }
          filterSelected={!!selectedGroups.length}
          onClearFilter={() => setSelectedGroups([])}
        >
          <CheckboxGroup
            options={allGroupsButAnnouncements.map(group => ({
              value: group,
              label: (
                <LabelM>
                  <FormattedMessage {...messages[group]} />
                </LabelM>
              ),
            }))}
            selectedValues={selectedGroups}
            onChange={setSelectedGroups}
          />
        </FilterInDropdown>
      </FiltersContainer>
      <ItemsContainer>
        {notifications?.nodes?.map(notification => {
          if (!notification) return null;
          return (
            <Notification key={notification.id} notification={notification} />
          );
        })}
        {data && !notifications?.nodes?.length && (
          <LabelM>
            <FormattedMessage
              id="Activity.Notifications.noNotifications"
              defaultMessage="You have no notifications yet."
            />
          </LabelM>
        )}
        <InfiniteScrollLoader />
      </ItemsContainer>
    </Root>
  );
};
